/**
 * Intentions de la page page-7-intentions :
 * - Intentions de PRIERE (priere-service)        → /api/micro/prieres/intentions
 * - Intentions de MESSE (sacrement-service)      → /api/micro/sacrement/mass-intentions
 *
 * On expose deux hooks distincts pour que la page choisisse la source.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export interface PrayerIntention {
  id: string;
  texte: string;
  nomAnonyme?: string;
  estPublique: boolean;
  voteCount?: number;
  parishId?: string;
  userId?: string;
  createdAt?: string;
  /**
   * Étape de relecture (IntentionResponse.statut) : EN_ATTENTE, VALIDEE,
   * REFUSEE ou PRESENTEE. Le mur public ne montre que VALIDEE et PRESENTEE ;
   * la file du secrétariat ne montre que EN_ATTENTE. Comme pour `notes` sur
   * MassIntention, le champ transitait déjà par le BFF sans être déclaré ici.
   */
  statut?: string;
  /** Renseigné pour un refus seulement, pour que l'auteur sache pourquoi. */
  motifRefus?: string;
  /** Horodatage de la décision du secrétariat. */
  decideeLe?: string;
  /** Fin de la fenêtre de lecture, vingt-quatre heures après le dépôt. */
  expireLe?: string;
  /**
   * Vrai quand la fenêtre est passée. Calculé par le service : ne jamais le
   * recalculer côté navigateur, le fuseau du poste n'est pas celui du diocèse.
   * Aucun écran ne s'en sert : la fenêtre est appliquée en amont par
   * `/intentions/a-porter`. Déclaré pour dire ce que la réponse contient.
   */
  expiree?: boolean;
}

export interface MassIntention {
  id: string;
  titre: string;
  pour?: string;
  dateMesse?: string;
  donateurNom?: string;
  amount?: number;
  currencyCode?: string;
  status?: string;
  paroisseId?: string;
  /**
   * Mention libre de l'intention (MassIntentionDTO.Response.notes, colonne
   * `sacrement.mass_intentions.notes`). CATHO+ y dépose « Déposée par un(e) … »
   * — et bientôt le nom de l'association ou du groupe au nom duquel le fidèle
   * demande la messe. Le champ transitait déjà par le BFF mais n'était pas
   * déclaré ici : sans lui le célébrant ne voit jamais au nom de qui la messe
   * est demandée. Absent pour les intentions saisies au secrétariat.
   */
  notes?: string;
  /** Horodatage de dépôt (MassIntentionDTO.Response.createdAt). */
  createdAt?: string;
  /** Heure de la messe quand elle est connue (LocalTime « HH:mm:ss »). */
  heureMesse?: string;
  /** Créneau paroissial retenu par le fidèle, ex. « dimanche|07:00 ». */
  horaireRef?: string;
  /** Compte CATHO+ du fidèle qui a déposé l'intention (prévenu des changements). */
  donateurId?: string;
  /** Canal d'origine : PAROISSE (secrétariat) ou CATHOPLUS (fidèle). */
  source?: string;
  /** Don ayant réglé l'intention (donation-service). */
  donationId?: string;
  /** Référence lisible du don, pour le rapprochement comptable. */
  donationReference?: string;
  /** Identifiant commun aux dates d'une même intention déposée en une fois. */
  groupeId?: string;
  /** Motif de la dernière annulation, tant que l'intention reste annulée. */
  motifAnnulation?: string;
  /** Horodatage de l'annulation en cours. */
  annuleeLe?: string;
  /** Horodatage du constat de célébration. */
  celebreeLe?: string;
  updatedAt?: string;
  /**
   * Historique des gestes du secrétariat (déplacement, annulation,
   * rétablissement, célébration). Renseigné par la lecture d'UNE intention
   * (GET /mass-intentions/:id) seulement, jamais par la liste.
   */
  historique?: MassIntentionEvenement[];
}

/** Un geste porté sur une intention de messe (sacrement-service). */
export interface MassIntentionEvenement {
  id: string;
  /** DEPLACEE, ANNULEE, RETABLIE ou CELEBREE. */
  action: string;
  statutAvant?: string;
  statutApres?: string;
  dateAvant?: string;
  heureAvant?: string;
  dateApres?: string;
  heureApres?: string;
  motif?: string;
  acteurRole?: string;
  survenuLe?: string;
}

export function usePrayerIntentions(
  parishId: string | null | undefined,
  opts: { publiquesUniquement?: boolean; page?: number; size?: number } = {},
) {
  const { publiquesUniquement = true, page = 0, size = 50 } = opts;
  return useQuery({
    queryKey: ["prayer-intentions", parishId, publiquesUniquement, page, size],
    enabled: !!parishId,
    queryFn: async () => {
      const params = new URLSearchParams({
        parishId: String(parishId),
        publiquesUniquement: String(publiquesUniquement),
        page: String(page),
        size: String(size),
      });
      const res = await apiRequest(
        "GET",
        `/api/micro/prieres/intentions?${params.toString()}`,
      );
      const data = await res.json();
      const items: PrayerIntention[] = Array.isArray(data) ? data : data.items ?? [];
      return items;
    },
  });
}

// ---------------------------------------------------------------------------
// Lecture EXHAUSTIVE des intentions de messe
//
// sacrement-service pagine et borne : `size` est ramené à 100 sur
// /sacrement/mass-intentions. Une feuille remise au célébrant ne peut pas
// dépendre de ce qui a tenu dans une page, d'autant que les intentions de
// messe arrivent triées par date de célébration DÉCROISSANTE : un carnet de
// réservations à quelques mois pousse la messe d'aujourd'hui hors de la
// première page. On lit donc jusqu'à épuisement.
//
// L'autre issue envisagée était de garder une seule page et d'avertir quand
// `total > items.length`. Elle a été écartée ICI : l'avertissement se serait
// allumé en permanence dès qu'une paroisse dépasse cent intentions dans son
// histoire, c'est-à-dire tout le temps, et un avertissement toujours allumé ne
// prévient plus de rien.
//
// Côté prières, ce problème n'existe plus : le service expose une liste déjà
// réduite à ce qui doit être porté à l'autel (voir `useIntentionsAPorter`), et
// l'avertissement y redevient l'exception qu'il doit être.
// ---------------------------------------------------------------------------

/** Borne haute de sacrement-service sur /mass-intentions (`Math.min(size, 100)`). */
const TAILLE_PAGE_MESSES = 100;

/**
 * Garde-fou : nombre maximal de pages parcourues. Vingt pages de cent lignes
 * couvrent très largement une année de paroisse. Au-delà on s'arrête et on le
 * DIT (`complet: false`) plutôt que de boucler indéfiniment sur un service qui
 * répondrait mal.
 */
const PAGES_MAX = 20;

export interface LectureExhaustive<T> {
  items: T[];
  /**
   * Faux quand la lecture s'est arrêtée sur le garde-fou avant d'avoir tout
   * ramené. L'écran et le document doivent alors le dire.
   */
  complet: boolean;
}

/** Enveloppe { items, total } du service, ou tableau nu des routes anciennes. */
function lireEnveloppe<T>(donnees: unknown): { items: T[]; total: number | null } {
  if (Array.isArray(donnees)) return { items: donnees as T[], total: null };
  const objet = donnees as { items?: unknown; total?: unknown } | null | undefined;
  return {
    items: Array.isArray(objet?.items) ? (objet.items as T[]) : [],
    total: typeof objet?.total === "number" ? objet.total : null,
  };
}

async function lireToutesLesPages<T>(
  lirePage: (page: number, taille: number) => Promise<{ items: T[]; total: number | null }>,
  taillePage: number,
): Promise<LectureExhaustive<T>> {
  const items: T[] = [];
  for (let page = 0; page < PAGES_MAX; page++) {
    const lot = await lirePage(page, taillePage);
    items.push(...lot.items);
    // Page incomplète : le service n'a plus rien à donner.
    if (lot.items.length < taillePage) return { items, complet: true };
    // Total serveur atteint : inutile d'en demander une de plus.
    if (lot.total !== null && items.length >= lot.total) return { items, complet: true };
  }
  return { items, complet: false };
}

/**
 * TOUTES les intentions de messe de la paroisse, page après page. Alimente la
 * liste de l'écran, la feuille du célébrant et la clôture après la messe : un
 * seul chargement, donc aucun risque que le document et le lot clôturé
 * portent sur des ensembles différents.
 */
export function useIntentionsDeMesseCompletes(parishId: string | null | undefined) {
  return useQuery({
    queryKey: ["mass-intentions-completes", parishId],
    enabled: !!parishId,
    queryFn: () =>
      lireToutesLesPages<MassIntention>(async (page, taille) => {
        const params = new URLSearchParams({
          parishId: String(parishId),
          page: String(page),
          size: String(taille),
        });
        const res = await apiRequest(
          "GET",
          `/api/micro/sacrement/mass-intentions?${params.toString()}`,
        );
        return lireEnveloppe<MassIntention>(await res.json());
      }, TAILLE_PAGE_MESSES),
  });
}

export function useMassIntentions(
  parishId: string | null | undefined,
  opts: { status?: string; page?: number; size?: number } = {},
) {
  const { status, page = 0, size = 50 } = opts;
  return useQuery({
    queryKey: ["mass-intentions", parishId, status, page, size],
    enabled: !!parishId,
    queryFn: async () => {
      const params = new URLSearchParams({
        parishId: String(parishId),
        page: String(page),
        size: String(size),
      });
      if (status) params.append("status", status);
      const res = await apiRequest(
        "GET",
        `/api/micro/sacrement/mass-intentions?${params.toString()}`,
      );
      const data = await res.json();
      const items: MassIntention[] = Array.isArray(data) ? data : data.items ?? [];
      return items;
    },
  });
}

/**
 * Compteur d'intentions de messe — lit le `total` SERVEUR de l'enveloppe
 * { items, page, size, total } (sacrement-service SacramentRecordsResource :
 * `total` = countMassIntentions(parishId, status), filtre `status` appliqué).
 *
 * Un KPI ne doit JAMAIS compter la longueur d'une page : le serveur borne
 * `size` à 100, donc `items.length` sature silencieusement. On demande donc
 * size=1 — seul le total est utilisé, jamais les lignes.
 */
export function useMassIntentionsCount(
  parishId: string | null | undefined,
  opts: { status?: string } = {},
) {
  const { status } = opts;
  return useQuery({
    queryKey: ["mass-intentions-count", parishId, status],
    enabled: !!parishId,
    queryFn: async () => {
      const params = new URLSearchParams({
        parishId: String(parishId),
        page: "0",
        size: "1",
      });
      if (status) params.append("status", status);
      const res = await apiRequest(
        "GET",
        `/api/micro/sacrement/mass-intentions?${params.toString()}`,
      );
      const data = await res.json();
      // L'API peut retourner un tableau nu (aucune enveloppe, donc aucun total
      // serveur : on se rabat sur la longueur reçue) ou { items, …, total }.
      const total: number = Array.isArray(data)
        ? data.length
        : Number(data?.total ?? (Array.isArray(data?.items) ? data.items.length : 0));
      return { total };
    },
  });
}

/** Corps de création d'une intention de messe (sacrement-service
 *  MassIntentionDTO.Request : titre + pour + dateMesse requis). */
export interface CreateMassIntentionBody {
  titre: string;
  pour: string;
  dateMesse: string; // LocalDate "AAAA-MM-JJ"
  heureMesse?: string; // LocalTime "HH:mm:ss"
  donateurNom?: string;
  amount?: number;
  currencyCode?: string;
  paroisseId?: string;
  notes?: string;
}

/**
 * Création d'une intention de messe — POST /api/micro/sacrement/mass-intentions
 * (upstream sacrement-service). Invalide la liste au succès.
 */
export function useCreateMassIntention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateMassIntentionBody) => {
      const res = await apiRequest("POST", "/api/micro/sacrement/mass-intentions", body);
      return (await res.json()) as MassIntention;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mass-intentions"] });
      // Le compteur du tableau de bord porte sa propre clé : sans elle, la
      // tuile restait figée (staleTime Infinity) après une saisie (09/10/2026).
      qc.invalidateQueries({ queryKey: ["mass-intentions-count"] });
      qc.invalidateQueries({ queryKey: ["mass-intentions-completes"] });
    },
  });
}

/** Corps de création d'une intention de prière (priere-service
 *  IntentionRequest : texte requis + nomAnonyme/estPublique/parishId). */
export interface CreatePrayerIntentionBody {
  texte: string;
  nomAnonyme?: string;
  estPublique?: boolean;
  parishId?: string;
}

/**
 * Création d'une intention de prière — POST /api/micro/prieres/intentions
 * (upstream priere-service). Utilisée par le portail fidèle (soumission d'un
 * paroissien). Invalide le mur de prières au succès.
 */
export function useCreatePrayerIntention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePrayerIntentionBody) => {
      const res = await apiRequest("POST", "/api/micro/prieres/intentions", body);
      return (await res.json()) as PrayerIntention;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prayer-intentions"] });
      qc.invalidateQueries({ queryKey: ["prayer-intentions-a-relire"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Relecture par le secrétariat
//
// Le circuit réel : le secrétariat relit les intentions déposées, écarte ce
// qui ne peut pas être lu à l'assemblée, puis marque « présentées » celles qui
// l'ont été. Côté messes, la bascule de statut suit le même geste.
//
// Chaque appel porte la paroisse concernée : le BFF refuse (403) une paroisse
// qui n'est pas celle de l'utilisateur.
// ---------------------------------------------------------------------------

/**
 * Enveloppe renvoyée par priere-service pour les listes du secrétariat.
 *
 * `total` est le compte SERVEUR de la liste entière, indépendant de la page
 * demandée : un compteur affiché ne doit jamais partir de `items.length`, la
 * page étant bornée. C'est aussi lui qui dit si l'écran voit tout.
 */
export interface PagePrieres {
  items: PrayerIntention[];
  total: number;
}

/**
 * Les deux listes du secrétariat ont la même enveloppe et le même contrat.
 * Une seule lecture, pour qu'elles ne divergent pas.
 */
async function lirePagePrieres(
  chemin: string,
  parishId: string,
  page: number,
  size: number,
): Promise<PagePrieres> {
  const params = new URLSearchParams({
    parishId,
    page: String(page),
    size: String(size),
  });
  const res = await apiRequest("GET", `${chemin}?${params.toString()}`);
  const data = await res.json();
  const items: PrayerIntention[] = Array.isArray(data) ? data : data?.items ?? [];
  const total: number = Array.isArray(data)
    ? data.length
    : Number(data?.total ?? items.length);
  return { items, total };
}

/**
 * File des intentions à relire — GET /api/micro/prieres/intentions/a-relire.
 * Réservée aux rôles paroissiaux (le BFF et le service le vérifient tous deux).
 *
 * Cette route a sa PROPRE borne, plus haute que celle du mur public :
 * priere-service y applique `Math.min(size, 200)` et non 100.
 */
export function useIntentionsARelire(
  parishId: string | null | undefined,
  opts: { page?: number; size?: number } = {},
) {
  const { page = 0, size = 50 } = opts;
  return useQuery({
    queryKey: ["prayer-intentions-a-relire", parishId, page, size],
    enabled: !!parishId,
    queryFn: (): Promise<PagePrieres> =>
      lirePagePrieres(
        "/api/micro/prieres/intentions/a-relire",
        String(parishId),
        page,
        size,
      ),
  });
}

/**
 * Ce qui doit être porté à l'autel — GET /api/micro/prieres/intentions/a-porter.
 *
 * C'est la feuille du célébrant, telle que le SERVICE la constitue : relue et
 * encore dans sa fenêtre de lecture, cloisonnée par paroisse, triée par date
 * de dépôt croissante. Elle alimente le document imprimé ET la clôture en lot,
 * pour qu'aucun des deux ne porte sur un autre ensemble que l'autre.
 *
 * Remplace une pagination sur `publiquesUniquement=false`, c'est-à-dire sur
 * l'historique ENTIER de la paroisse, pour n'en retenir qu'une poignée : le
 * tri appartient au service, qui a l'index et la fenêtre de lecture. Ce
 * paramètre répond d'ailleurs désormais 403 hors du secrétariat, là où il
 * repliait silencieusement sur le mur public.
 *
 * Borne du service : `Math.min(size, 200)`. Quand `total` dépasse ce qui est
 * revenu, l'écran et le document le disent (voir la page Intentions).
 */
export function useIntentionsAPorter(
  parishId: string | null | undefined,
  opts: { page?: number; size?: number } = {},
) {
  const { page = 0, size = 200 } = opts;
  return useQuery({
    queryKey: ["prayer-intentions-a-porter", parishId, page, size],
    enabled: !!parishId,
    queryFn: (): Promise<PagePrieres> =>
      lirePagePrieres(
        "/api/micro/prieres/intentions/a-porter",
        String(parishId),
        page,
        size,
      ),
  });
}

/**
 * Invalide la file de relecture, le mur public ET la feuille du célébrant :
 * une décision touche les trois. Sans la troisième, la feuille continuerait
 * d'ignorer l'intention qui vient d'être validée.
 */
function useInvaliderIntentions() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["prayer-intentions-a-relire"] });
    qc.invalidateQueries({ queryKey: ["prayer-intentions-a-porter"] });
    qc.invalidateQueries({ queryKey: ["prayer-intentions"] });
  };
}

/** Validation d'une intention — POST .../intentions/:id/valider. */
export function useValiderIntention() {
  const invalider = useInvaliderIntentions();
  return useMutation({
    mutationFn: async ({ id, parishId }: { id: string; parishId: string }) => {
      const res = await apiRequest(
        "POST",
        `/api/micro/prieres/intentions/${id}/valider?parishId=${encodeURIComponent(parishId)}`,
      );
      return (await res.json()) as PrayerIntention;
    },
    onSuccess: invalider,
  });
}

/**
 * Refus d'une intention — POST .../intentions/:id/refuser.
 * Le motif est obligatoire côté service : un refus muet n'aide personne.
 */
export function useRefuserIntention() {
  const invalider = useInvaliderIntentions();
  return useMutation({
    mutationFn: async ({
      id,
      parishId,
      motif,
    }: {
      id: string;
      parishId: string;
      motif: string;
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/micro/prieres/intentions/${id}/refuser?parishId=${encodeURIComponent(parishId)}`,
        { motif },
      );
      return (await res.json()) as PrayerIntention;
    },
    onSuccess: invalider,
  });
}

/**
 * Bascule en lot après la célébration — POST .../intentions/presentees.
 * En lot et non une par une, parce que c'est ainsi que le travail se fait.
 */
export function useMarquerPresentees() {
  const invalider = useInvaliderIntentions();
  return useMutation({
    mutationFn: async ({
      ids,
      parishId,
    }: {
      ids: string[];
      parishId: string;
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/micro/prieres/intentions/presentees?parishId=${encodeURIComponent(parishId)}`,
        { ids },
      );
      return (await res.json()) as { items: PrayerIntention[]; total: number };
    },
    onSuccess: invalider,
  });
}

/**
 * Invalide la liste des messes, son compteur, la lecture exhaustive et la
 * fiche ouverte. `mass-intentions` couvre aussi le fil d'activité du tableau
 * de bord, et `mass-intentions-count` sa tuile « intentions planifiées ».
 */
function useInvaliderMesses() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["mass-intentions"] });
    qc.invalidateQueries({ queryKey: ["mass-intentions-count"] });
    qc.invalidateQueries({ queryKey: ["mass-intentions-completes"] });
    qc.invalidateQueries({ queryKey: ["mass-intention"] });
  };
}

/**
 * Lecture d'UNE intention de messe, avec son historique : GET
 * /api/micro/sacrement/mass-intentions/:id?parishId=… La paroisse voyage pour
 * que le BFF et le service refusent la fiche d'une autre paroisse.
 */
export function useMassIntention(
  id: string | null | undefined,
  parishId: string | null | undefined,
) {
  return useQuery({
    queryKey: ["mass-intention", id, parishId],
    enabled: !!id && !!parishId,
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/micro/sacrement/mass-intentions/${id}?parishId=${encodeURIComponent(String(parishId))}`,
      );
      const data = await res.json();
      // Seule une intention d'identifiant attendu fait foi : toute autre forme
      // de réponse laisse la fiche sur les données de la liste.
      return data && !Array.isArray(data) && data.id === id
        ? (data as MassIntention)
        : null;
    },
  });
}

/**
 * Déplace une messe (nouvelle date, heure facultative) : PATCH
 * /api/micro/sacrement/mass-intentions/:id/deplacer. Le service n'accepte
 * qu'une intention planifiée, ne touche ni au montant ni au donateur, et
 * consigne l'ancienne date dans l'historique.
 */
export function useDeplacerMesse() {
  const invalider = useInvaliderMesses();
  return useMutation({
    mutationFn: async ({
      id,
      parishId,
      dateMesse,
      heureMesse,
      horaireRef,
    }: {
      id: string;
      parishId: string;
      dateMesse: string; // AAAA-MM-JJ
      heureMesse?: string; // HH:mm:ss, absente = date seule
      /** Messe publiée retenue (« dimanche|09:00 ») ; absente = heure libre ou date seule. */
      horaireRef?: string;
    }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/micro/sacrement/mass-intentions/${id}/deplacer`,
        { parishId, dateMesse, heureMesse: heureMesse ?? null, horaireRef: horaireRef ?? null },
      );
      return (await res.json()) as MassIntention;
    },
    onSuccess: invalider,
  });
}

/**
 * Change le SEUL statut d'une intention de messe — PATCH
 * /api/micro/sacrement/mass-intentions/:id/statut.
 *
 * Distinct du PUT de mise à jour, qui écrit aussi le montant et le donateur :
 * le secrétariat constate qu'une messe a été célébrée sans pouvoir toucher à
 * la somme versée. Transitions acceptées par le service : PLANIFIEE vers
 * CELEBREE ou ANNULEE, ANNULEE vers PLANIFIEE (rétablir). Rien depuis
 * CELEBREE. Une annulation exige un motif, conservé sur l'intention et dans
 * son historique.
 */
export function useChangerStatutMesse() {
  const invalider = useInvaliderMesses();
  return useMutation({
    mutationFn: async ({
      id,
      parishId,
      status,
      motif,
    }: {
      id: string;
      parishId: string;
      status: string;
      /** Obligatoire pour ANNULEE : le service refuse (400) sans lui. */
      motif?: string;
    }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/micro/sacrement/mass-intentions/${id}/statut`,
        motif ? { status, parishId, motif } : { status, parishId },
      );
      return (await res.json()) as MassIntention;
    },
    onSuccess: invalider,
  });
}

/**
 * Clôt en une fois les intentions d'une célébration — POST
 * /api/micro/sacrement/mass-intentions/celebrees. Tout ou rien côté service.
 */
export function useMarquerMessesCelebrees() {
  const invalider = useInvaliderMesses();
  return useMutation({
    mutationFn: async ({
      ids,
      parishId,
    }: {
      ids: string[];
      parishId: string;
    }) => {
      const res = await apiRequest(
        "POST",
        "/api/micro/sacrement/mass-intentions/celebrees",
        { ids, parishId },
      );
      return (await res.json()) as { items: MassIntention[]; total: number };
    },
    onSuccess: invalider,
  });
}
